import { Component, OnInit } from '@angular/core';
import { AddProductCartComponent } from 'src/app/elements/add-product-cart/add-product-cart.component';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { ProductService } from 'src/app/services/product.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Product } from 'src/app/entities/product';
import { CartService } from 'src/app/services/cart.service';
import { PaymentInstallmentComponent } from 'src/app/elements/view-mount-card/view-mount-card.component';
import { slugify } from 'src/app/utils/string.utils';
import { Location } from '@angular/common';

@Component({
  selector: 'app-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss']
})
export class ProductDetailsComponent implements OnInit {

  product = new Product();
  productImages = [];
  imageShow = '';
  urlYoutube;
  quantity = 1;
  discount = new Product();
  price = this.getPrice();
  
  // Shipping calculator properties
  zipCode = '';
  shippingResult: any = null;
  shippingError = '';
  isCalculatingShipping = false;

  // Product specifications
  productSpecs: any[] = [];

  // Store the original param to check if we need to update URL
  private originalParam: string = '';

  constructor(
    protected productService: ProductService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected dialog: MatDialog, 
    protected sanitizer: DomSanitizer,
    protected cartService: CartService,
    protected location: Location) {

  }

  addItemToCart() {
    this.dialog.open(AddProductCartComponent, {
      width: '400px',
      data: { product: this.product, quantity: this.quantity }
    });
  }

  viewMountToCard() {
    this.dialog.open(PaymentInstallmentComponent, {
      width: '800px',
      data: { product: this.product, quantity: this.quantity, discount: this.discount }
    });
  }

  sumQuantity() {
    if (this.quantity >= this.getStock()) {
      return;
    }
    this.quantity++;
  }

  minQuantity() {
    if (this.quantity <= 1) {
      this.quantity = 1;
      return;
    }
    this.quantity--;
  }

  ngOnInit() {
    this.loadParams();
  }

  getPrice() {
    return Product.getPrice(this.product);
  }

  getPriceTwo(){
    return Product.getPriceTwo(this.product);
  }

  getPriceWithoutDiscount() {
    return Product.getPriceWithoutDiscount(this.product);
  }

  getDiscount() {
    return Product.getDiscount(this.product);
  }

  getStock() {
    return Product.getStock(this.product);
  }

  getChildId() {
    return this.product.childs[0].id;
  }

  loadProduct(productId: number) {
    this.productService.fetchById(productId).toPromise().then(data => {
      if (data.success) {
        this.product = data.response;
        this.processImages();
        this.processSpecifications();
        this.updateUrlIfNeeded();
      }
    });
  }

  processSpecifications() {
    if (!this.product.caption) {
      return;
    }

    // Parse the HTML caption to extract specifications
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.product.caption;
    const lines = tempDiv.textContent?.split('\n').filter(line => line.trim()) || [];

    // Icon mapping for different spec types using Feather Icons
    const iconMap: { [key: string]: string } = {
      'TAMAÑO': 'monitor',
      'PANTALLA': 'monitor',
      'RESOLUCION': 'image',
      'RESOLUCIÓN': 'image',
      'SONIDO': 'volume-2',
      'SMART': 'smartphone',
      'DISEÑO': 'edit-3',
      'WI FI': 'wifi',
      'WIFI': 'wifi',
      'BLUETOOTH': 'bluetooth',
      'USB': 'share-2',
      'HDMI': 'cast',
      'ALTO': 'arrow-up',
      'ANCHO': 'arrow-right',
      'PROFUNDIDAD': 'maximize',
      'PESO': 'activity',
      'RODADO': 'disc',
      'TALLE': 'maximize-2',
      'ESTATURA': 'user',
      'CUADRO': 'square',
      'HORQUILLA': 'tool',
      'SUSPENSION': 'tool',
      'SUSPENSIÓN': 'tool',
      'VELOCIDADES': 'zap',
      'STEM': 'settings',
      'SHIFTERS': 'sliders',
      'CAMBIO': 'sliders',
      'DESCARRILADOR': 'sliders',
      'LLANTAS': 'disc',
      'FRENOS': 'octagon',
      'ASIENTO': 'circle',
      'CAJA': 'box',
      'ACCESORIOS': 'plus-circle',
      'BATERIA': 'battery-charging',
      'BATERÍA': 'battery-charging',
      'CAMARA': 'camera',
      'CÁMARA': 'camera',
      'MEMORIA': 'hard-drive',
      'PROCESADOR': 'cpu',
      'SISTEMA': 'monitor',
      'CONECTIVIDAD': 'wifi',
      'PUERTO': 'circle',
      'ENTRADA': 'log-in',
      'SALIDA': 'log-out',
      'UNIDAD': 'package',
      'BULTO': 'package'
    };

    // Section headers that indicate a new group (bulto/package)
    const sectionKeywords = ['UNIDAD INTERIOR', 'UNIDAD EXTERIOR', 'BULTO', 'PAQUETE'];
    
    let currentSection = null;
    const groupedSpecs = [];

    lines.forEach(line => {
      const lineUpper = line.trim().toUpperCase();
      
      // Check if this line is a section header (ends with : and matches keywords)
      const isSectionHeader = sectionKeywords.some(keyword => 
        lineUpper.includes(keyword) && (lineUpper.endsWith(':') || !lineUpper.includes(': '))
      );

      if (isSectionHeader) {
        // Start a new section
        currentSection = {
          title: line.trim().replace(/:/g, ''),
          icon: 'package',
          specs: []
        };
        groupedSpecs.push(currentSection);
      } else {
        const parts = line.split(':');
        let label: string;
        let value: string;
        
        // Check if it has the "label: value" format
        if (parts.length >= 2) {
          label = parts[0].trim();
          value = parts.slice(1).join(':').trim();
        } else {
          // If no colon, treat entire text as the value with generic label
          label = '';
          value = line.trim();
        }
        
        if (!value) {
          return; // Skip empty values
        }
        
        // Find matching icon based on the text content
        let icon = 'info';
        const searchText = (label + ' ' + value).toUpperCase();
        for (const key in iconMap) {
          if (searchText.includes(key)) {
            icon = iconMap[key];
            break;
          }
        }

        const spec = {
          label: label,
          value: value,
          icon: icon
        };

        // Add to current section or create general section
        if (currentSection) {
          currentSection.specs.push(spec);
        } else {
          // Create a general section for ungrouped specs
          if (groupedSpecs.length === 0 || groupedSpecs[0].title) {
            groupedSpecs.unshift({
              title: null,
              icon: null,
              specs: []
            });
          }
          groupedSpecs[0].specs.push(spec);
        }
      }
    });

    this.productSpecs = groupedSpecs;

    // Initialize Feather icons after specs are loaded
    setTimeout(() => {
      if (typeof (window as any).feather !== 'undefined') {
        (window as any).feather.replace();
      }
    }, 0);
  }

  clickPhoto(image) {
    this.imageShow = image;
  }

  processImages() {
    this.imageShow = this.product.photo_feature;

    this.productImages = [];
    
    if (this.product.photo_feature) {
      this.productImages.push(this.product.photo_feature);
    }
    if (this.product.photos) {
      for (const item of this.product.photos) {
        this.productImages.push(item.url);
      }
    }

    if (this.product.video != '' && this.product.video != undefined && this.product.video != null) {
      this.urlYoutube = this.sanitizer.bypassSecurityTrustResourceUrl(this.product.video);
    }
  }

  loadParams() {
    this.route.params.subscribe(params => {
      if (params.id) {
        this.originalParam = params.id;
        this.loadProduct(params.id);
      } else {
        this.router.navigateByUrl('/pagina-no-encontrada');
      }
    });
  }

  /**
   * Updates the URL to the proper format with numeric ID and slug
   * without triggering a new navigation/request
   */
  private updateUrlIfNeeded() {
    // Check if the original param is not a numeric ID (i.e., it's an external code)
    const isNumeric = /^\d+$/.test(this.originalParam);
    
    if (!isNumeric && this.product.id > 0) {
      // Build the proper URL with numeric ID and slug
      const slug = slugify(this.product.title);
      const newUrl = `/producto/${this.product.id}/${slug}`;
      
      // Replace the current URL without triggering navigation
      this.location.replaceState(newUrl);
    }
  }

  calculateShipping() {
    // Reset previous results
    this.shippingResult = null;
    this.shippingError = '';

    // Validate zip code
    if (!this.zipCode || this.zipCode.trim() === '') {
      this.shippingError = 'Por favor ingrese un código postal';
      return;
    }

    if (this.zipCode.length < 4) {
      this.shippingError = 'El código postal debe tener al menos 4 dígitos';
      return;
    }

    // Start loading
    this.isCalculatingShipping = true;

    // Call the service
    this.productService.calculateShipping(this.product.id, this.zipCode, this.quantity)
      .toPromise()
      .then(response => {
        this.isCalculatingShipping = false;
        
        if (response.success) {
          this.shippingResult = response.response;
        } else {
          // Handle error from API
          if (response.error && response.error.message) {
            this.shippingError = response.error.message;
          } else {
            this.shippingError = 'No se pudo calcular el envío. Intente nuevamente.';
          }
        }
      })
      .catch(error => {
        this.isCalculatingShipping = false;
        this.shippingError = 'Error al calcular el envío. Por favor intente nuevamente.';
        console.error('Shipping calculation error:', error);
      });
  }

  onlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }
}
